import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    AccountingPeriod,
    ClosePreview,
    FiscalYear,
    JournalEntry,
    LocaleCode,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useCloseFiscalYearMutation,
    useClosePeriodMutation,
    useFiscalYearClosePreviewQuery,
    useFiscalYearPeriodsQuery,
    useFiscalYearsQuery,
    useReopenFiscalYearMutation,
    useReopenPeriodMutation,
} from '@/api/queries';
import {
    getEarlierOpenCounts,
    getLaterClosedCounts,
    getRolloverAvailability,
    getYearCloseAvailability,
    type YearCloseAvailability,
} from './periodsMeta';

const SUPPORTED_LOCALES: LocaleCode[] = ['ka', 'en'];
const resolveLocale = (language: string): LocaleCode =>
    (SUPPORTED_LOCALES as string[]).includes(language)
        ? (language as LocaleCode)
        : 'en';

/** Which confirmation dialog is open, if any. */
export type PeriodsDialog =
    | { kind: 'CLOSE_PERIOD'; period: AccountingPeriod }
    | { kind: 'REOPEN_PERIOD'; period: AccountingPeriod }
    | { kind: 'CLOSE_YEAR' }
    | { kind: 'ROLLOVER' }
    | { kind: 'REOPEN_YEAR' };

/**
 * What the last committed year-end action produced, so the screen can link
 * straight to the audit trail and offer the next step. Cleared when the
 * selected year changes.
 */
export type YearActionResult =
    | { kind: 'CLOSED'; entry: JournalEntry | null }
    | { kind: 'REOPENED'; entries: JournalEntry[] };

export const usePeriodsPage = () => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    // ── fiscal years ────────────────────────────────────────────────────────
    const yearsQuery = useFiscalYearsQuery();
    const yearsResponse = yearsQuery.data;
    // Memoized so the empty fallback keeps a stable identity — otherwise the
    // default-year effect below re-runs on every render.
    const fiscalYears: FiscalYear[] = React.useMemo(
        () => (yearsResponse?.success ? yearsResponse.response.items : []),
        [yearsResponse],
    );

    const [selectedYearId, setSelectedYearId] = React.useState<string | null>(
        null,
    );
    // Default to the newest year once the list arrives, and recover if the
    // selected year disappears.
    React.useEffect(() => {
        if (fiscalYears.length === 0) {
            return;
        }
        const stillThere = fiscalYears.some(
            (year) => year.id === selectedYearId,
        );
        if (!stillThere) {
            setSelectedYearId(fiscalYears[0]!.id);
        }
    }, [fiscalYears, selectedYearId]);

    // ── periods of the selected year ────────────────────────────────────────
    const periodsQuery = useFiscalYearPeriodsQuery(selectedYearId);
    const periodsResponse = periodsQuery.data;
    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const periods: AccountingPeriod[] = React.useMemo(
        () => (periodsResponse?.success ? periodsResponse.response.items : []),
        [periodsResponse],
    );
    // The year echoed back by the periods call is the freshest copy — the list
    // query may still be serving a pre-close snapshot.
    const selectedYear: FiscalYear | null = periodsResponse?.success
        ? periodsResponse.response.fiscalYear
        : (fiscalYears.find((year) => year.id === selectedYearId) ?? null);

    const loadError: ErrorResponse | null =
        (yearsResponse && !yearsResponse.success
            ? yearsResponse.error
            : null) ??
        (periodsResponse && !periodsResponse.success
            ? periodsResponse.error
            : null);
    const isLoading = yearsQuery.isLoading || periodsQuery.isLoading;

    // Depend on the refetch functions, not the query objects: those get a new
    // identity every render, so the memo would never hold.
    const refetchYears = yearsQuery.refetch;
    const refetchPeriods = periodsQuery.refetch;
    const onRetry = React.useCallback(() => {
        void refetchYears();
        void refetchPeriods();
    }, [refetchYears, refetchPeriods]);

    // ── availability (mirrors the server's own gates) ───────────────────────
    const yearClose: YearCloseAvailability = getYearCloseAvailability(
        selectedYear,
        periods,
    );
    const rollover: YearCloseAvailability =
        getRolloverAvailability(selectedYear);
    /**
     * Per-row close eligibility. The server closes periods strictly in order, so
     * a row is only closeable once every earlier one is closed; computed once
     * for the year rather than per row.
     */
    const earlierOpenCounts = React.useMemo(
        () => getEarlierOpenCounts(periods),
        [periods],
    );

    /**
     * Whether "periods close in order" applies to anything on this table, so
     * the View can state it once above the rows instead of on each of them
     * (BOS-170).
     */
    const showsClosesInOrderNotice = React.useMemo(
        () => Object.values(earlierOpenCounts).some((count) => count > 0),
        [earlierOpenCounts],
    );
    /**
     * Per-row reopen eligibility — the mirror of the above. The server reopens
     * periods newest-first, so only the newest closed one may reopen; every
     * other closed row states which period has to move before it can.
     */
    const laterClosedCounts = React.useMemo(
        () => getLaterClosedCounts(periods),
        [periods],
    );
    const isYearSettled =
        selectedYear !== null && selectedYear.status !== 'OPEN';

    // ── dialogs + results ───────────────────────────────────────────────────
    const [dialog, setDialog] = React.useState<PeriodsDialog | null>(null);
    const [result, setResult] = React.useState<YearActionResult | null>(null);
    const [actionError, setActionError] = React.useState<ErrorResponse | null>(
        null,
    );

    // Nothing leaks across years.
    React.useEffect(() => {
        setDialog(null);
        setResult(null);
        setActionError(null);
    }, [selectedYearId]);

    // Opening or closing a dialog drops any error from the last attempt — a
    // failed period close must not greet the user inside the year-close dialog.
    const onOpenDialog = React.useCallback((next: PeriodsDialog) => {
        setActionError(null);
        setDialog(next);
    }, []);
    const onCloseDialog = React.useCallback(() => {
        setActionError(null);
        setDialog(null);
    }, []);
    const onDismissResult = React.useCallback(() => setResult(null), []);

    // ── the dry run, fetched only while its dialog is open ──────────────────
    //
    // There is exactly one. `/ledger/fiscal-years/{year}/rollover/preview` does
    // not exist, so the rollover has no dry run to approve and its dialog is
    // `LedgerHandoffNotice` rather than a preview — see `PeriodsPageView`.
    const closePreviewQuery = useFiscalYearClosePreviewQuery(
        selectedYearId,
        dialog?.kind === 'CLOSE_YEAR',
        locale,
    );
    const closePreviewResponse = closePreviewQuery.data;

    const closePreview: ClosePreview | null = closePreviewResponse?.success
        ? closePreviewResponse.response
        : null;
    const closePreviewError =
        closePreviewResponse && !closePreviewResponse.success
            ? closePreviewResponse.error
            : null;

    const closePeriod = useClosePeriodMutation();
    const reopenPeriod = useReopenPeriodMutation();
    const closeYear = useCloseFiscalYearMutation();
    const reopenYear = useReopenFiscalYearMutation();

    const onConfirmClosePeriod = React.useCallback(async () => {
        if (dialog?.kind !== 'CLOSE_PERIOD') {
            return;
        }
        setActionError(null);
        const response = await closePeriod.mutateAsync(dialog.period.id);
        if (!response.success) {
            setActionError(response.error);
            return;
        }
        setDialog(null);
    }, [closePeriod, dialog]);

    const onConfirmReopenPeriod = React.useCallback(async () => {
        if (dialog?.kind !== 'REOPEN_PERIOD') {
            return;
        }
        setActionError(null);
        const response = await reopenPeriod.mutateAsync(dialog.period.id);
        if (!response.success) {
            setActionError(response.error);
            return;
        }
        setDialog(null);
    }, [dialog, reopenPeriod]);

    const onConfirmCloseYear = React.useCallback(async () => {
        if (!selectedYearId) {
            return;
        }
        setActionError(null);
        const response = await closeYear.mutateAsync(selectedYearId);
        if (!response.success) {
            setActionError(response.error);
            return;
        }
        setDialog(null);
        setResult({ kind: 'CLOSED', entry: response.response.closingEntry });
    }, [closeYear, selectedYearId]);

    const onConfirmReopenYear = React.useCallback(async () => {
        if (!selectedYearId) {
            return;
        }
        setActionError(null);
        const response = await reopenYear.mutateAsync(selectedYearId);
        if (!response.success) {
            setActionError(response.error);
            return;
        }
        setDialog(null);
        setResult({
            kind: 'REOPENED',
            entries: response.response.reversalEntries,
        });
    }, [reopenYear, selectedYearId]);

    return {
        locale,
        // data
        fiscalYears,
        selectedYearId,
        selectedYear,
        periods,
        isLoading,
        loadError,
        onRetry,
        onSelectYear: setSelectedYearId,
        // availability
        yearClose,
        rollover,
        earlierOpenCounts,
        showsClosesInOrderNotice,
        laterClosedCounts,
        isYearSettled,
        // dialogs
        dialog,
        onOpenDialog,
        onCloseDialog,
        actionError,
        // the one dry run
        closePreview,
        closePreviewError,
        isClosePreviewLoading: closePreviewQuery.isLoading,
        // commits
        onConfirmClosePeriod,
        onConfirmReopenPeriod,
        onConfirmCloseYear,
        onConfirmReopenYear,
        isClosingPeriod: closePeriod.isPending,
        isReopeningPeriod: reopenPeriod.isPending,
        isClosingYear: closeYear.isPending,
        isReopeningYear: reopenYear.isPending,
        // result of the last year-end action
        result,
        onDismissResult,
    };
};

export type PeriodsPageModel = ReturnType<typeof usePeriodsPage>;
